import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApiService } from '../../services/api';
import './OrderTracking.css';

const OrderTracking = () => {
  const { t } = useTranslation();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApiService();

  // Extract order_id from URL query parameters (optional)
  const queryParams = new URLSearchParams(location.search);
  const urlOrderId = queryParams.get('order_id');

  useEffect(() => {
    // If order ID is provided in URL, set it in the input field
    if (urlOrderId) {
      setOrderNumber(urlOrderId);
    }
  }, [urlOrderId]);

  const fetchOrderDetails = async (orderId) => {
    if (!orderId) {
      setError(t('order_tracking.error.no_order_id'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.translations.getOrderDetails(orderId);
      setOrderDetails(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError(t('order_tracking.error.fetch_failed'));
      setOrderNumber(''); // Clear the input field on error
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchOrderDetails(orderNumber.trim());
  };

  const handleOrderNumberChange = (e) => {
    setOrderNumber(e.target.value);
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  // Function to format date string
  const formatDate = (dateString) => {
    if (!dateString) return t('order_tracking.not_available');
    const date = new Date(dateString);
    return date.toLocaleDateString(t('locale'), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Function to get status label and class
  const getStatusInfo = (status) => {
    const statusMap = {
      'unpaid': { label: t('order_tracking.status.unpaid'), className: 'status-unpaid' },
      'paid': { label: t('order_tracking.status.paid'), className: 'status-paid' },
      'in_progress': { label: t('order_tracking.status.in_progress'), className: 'status-in-progress' },
      'completed': { label: t('order_tracking.status.completed'), className: 'status-completed' },
      'cancelled': { label: t('order_tracking.status.cancelled'), className: 'status-cancelled' },
    };

    return statusMap[status] || { label: status, className: '' };
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const renderOrderForm = () => {
    return (
      <div className="order-tracking-form">
        <h2>{t('order_tracking.enter_order')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="orderNumber">{t('order_tracking.order_number')}</label>
            <input
              type="text"
              id="orderNumber"
              value={orderNumber}
              onChange={handleOrderNumberChange}
              placeholder={t('order_tracking.order_number_placeholder')}
              className={error ? 'error-input' : ''}
              required
            />
            {error && (
              <div className="inline-error-message">
                {error}
              </div>
            )}
          </div>
          <button type="submit" className="primary-button" disabled={loading}>
            {loading ? t('order_tracking.loading') : t('order_tracking.track_button')}
          </button>
        </form>
      </div>
    );
  };

  return (
    <div className="order-tracking-page">
      <div className="order-tracking-container">
        <div className="order-tracking-header">
          <h1>{t('order_tracking.title')}</h1>
          <p>{t('order_tracking.subtitle')}</p>
        </div>

        <div className="content-wrapper">
          {!orderDetails && renderOrderForm()}

          {orderDetails && (
            <div className="order-details-wrapper">
              {loading && (
                <div className="page-loader">
                  <div className="loader-container">
                    <div className="loader-animation"></div>
                    <p>{t('order_tracking.loading')}</p>
                  </div>
                </div>
              )}
              <div className="order-details-card">
                <div className="order-header">
                  <div className="order-id">
                    <h2>{t('order_tracking.order_id')}</h2>
                    <p>{orderDetails.id}</p>
                  </div>
                  <div className={`order-status ${getStatusInfo(orderDetails.status).className}`}>
                    <span>{getStatusInfo(orderDetails.status).label}</span>
                  </div>
                </div>

                <div className="order-info-grid">
                  <div className="info-item">
                    <h3>{t('order_tracking.document')}</h3>
                    <p>{orderDetails.original_filename}</p>
                    {orderDetails.document && (
                      <a 
                        href={orderDetails.document} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="document-link"
                      >
                        {t('order_tracking.download_original')}
                      </a>
                    )}
                  </div>

                  <div className="info-item">
                    <h3>{t('order_tracking.translation')}</h3>
                    {orderDetails.translated_document ? (
                      <a 
                        href={orderDetails.translated_document} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="document-link"
                      >
                        {t('order_tracking.download_translation')}
                      </a>
                    ) : (
                      <p>{t('order_tracking.translation_not_ready')}</p>
                    )}
                  </div>

                  <div className="info-item">
                    <h3>{t('order_tracking.languages')}</h3>
                    <p>
                      <span className="label">{t('order_tracking.from')}:</span> {orderDetails.source_language_name}
                    </p>
                    <p>
                      <span className="label">{t('order_tracking.to')}:</span> {orderDetails.target_language_name}
                    </p>
                  </div>

                  <div className="info-item">
                    <h3>{t('order_tracking.type')}</h3>
                    <p>{orderDetails.translation_type_name}</p>
                  </div>

                  <div className="info-item">
                    <h3>{t('order_tracking.word_count')}</h3>
                    <p>{orderDetails.word_count}</p>
                  </div>

                  <div className="info-item">
                    <h3>{t('order_tracking.price')}</h3>
                    <p>{orderDetails.total_price}</p>
                  </div>

                  <div className="info-item">
                    <h3>{t('order_tracking.dates')}</h3>
                    <p>
                      <span className="label">{t('order_tracking.created')}:</span> {formatDate(orderDetails.created_at)}
                    </p>
                    <p>
                      <span className="label">{t('order_tracking.completed')}:</span> {formatDate(orderDetails.completed_at)}
                    </p>
                  </div>

                  <div className="info-item notes-section">
                    <h3>{t('order_tracking.notes')}</h3>
                    <p>{orderDetails.customer_notes || t('order_tracking.no_notes')}</p>
                    
                    {orderDetails.translator_notes && (
                      <>
                        <h4>{t('order_tracking.translator_notes')}</h4>
                        <p>{orderDetails.translator_notes}</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="order-actions">
                  <button className="primary-button" onClick={() => setOrderDetails(null)}>
                    {t('order_tracking.track_another')}
                  </button>
                  <button className="secondary-button" onClick={handleBackToHome}>
                    {t('order_tracking.back_to_home')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;